// src/api/adminService.js
// Superadmin API service for access requests and markets management

import { apiFetch } from "./http";
import { API_ENDPOINTS } from "../config";

/**
 * List access requests with optional filters and pagination
 */
export async function listAccessRequests({ page = 1, page_size = 20, status_filter, market_id } = {}) {
  const params = new URLSearchParams();
  params.set("page", page);
  params.set("page_size", page_size);
  if (status_filter && status_filter !== "all") params.set("status", status_filter);
  if (market_id) params.set("market_id", market_id);

  const res = await apiFetch(`${API_ENDPOINTS.ADMIN_ACCESS_REQUESTS}?${params.toString()}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to list access requests (${res.status})`);
  }
  return res.json();
}

/**
 * Get a single access request by ID
 */
export async function getAccessRequest(id) {
  const res = await apiFetch(`${API_ENDPOINTS.ADMIN_ACCESS_REQUESTS}/${id}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to get access request (${res.status})`);
  }
  return res.json();
}

/**
 * Approve an access request
 */
export async function approveRequest(id, { org_name, expires_in_days = 7, admin_notes } = {}) {
  const res = await apiFetch(`${API_ENDPOINTS.ADMIN_ACCESS_REQUESTS}/${id}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ org_name, expires_in_days, admin_notes }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to approve request (${res.status})`);
  }
  return res.json();
}

/**
 * Reject an access request
 */
export async function rejectRequest(id, { admin_notes } = {}) {
  const res = await apiFetch(`${API_ENDPOINTS.ADMIN_ACCESS_REQUESTS}/${id}/reject`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ admin_notes }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to reject request (${res.status})`);
  }
  return res.json();
}

/**
 * List all markets (admin view)
 */
export async function listMarkets() {
  const res = await apiFetch(API_ENDPOINTS.ADMIN_MARKETS);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to list markets (${res.status})`);
  }
  return res.json();
}

/**
 * Create a new market
 */
export async function createMarket({ city, state, capacity = 10, status = "open" }) {
  const res = await apiFetch(API_ENDPOINTS.ADMIN_MARKETS, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ city, state, capacity, status }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to create market (${res.status})`);
  }
  return res.json();
}

/**
 * Update an existing market
 */
/**
 * Resend invite for an approved access request
 */
export async function resendInvite(id, { expires_in_days = 7 } = {}) {
  const res = await apiFetch(
    `${API_ENDPOINTS.ADMIN_ACCESS_REQUESTS}/${id}/resend-invite?expires_in_days=${expires_in_days}`,
    { method: "POST" }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to resend invite (${res.status})`);
  }
  return res.json();
}

/**
 * List organizations with optional filters and pagination
 */
export async function listOrganizations({ search, subscription_plan, market_id, page = 1, page_size = 50 } = {}) {
  const params = new URLSearchParams();
  params.set("page", page);
  params.set("page_size", page_size);
  if (search) params.set("search", search);
  if (subscription_plan) params.set("subscription_plan", subscription_plan);
  if (market_id) params.set("market_id", market_id);

  const res = await apiFetch(`${API_ENDPOINTS.ADMIN_ORGANIZATIONS}?${params.toString()}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to list organizations (${res.status})`);
  }
  return res.json();
}

/**
 * Get a single organization by ID
 */
export async function getOrganization(id) {
  const res = await apiFetch(`${API_ENDPOINTS.ADMIN_ORGANIZATIONS}/${id}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to get organization (${res.status})`);
  }
  return res.json();
}

export async function updateMarket(id, { capacity, status }) {
  const res = await apiFetch(`${API_ENDPOINTS.ADMIN_MARKETS}/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ capacity, status }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to update market (${res.status})`);
  }
  return res.json();
}

/**
 * Update trial/subscription for all users in an organization
 */
export async function updateOrgSubscription(orgId, { trial_ends_at, subscription_status, is_active } = {}) {
  const body = {};
  if (trial_ends_at !== undefined) body.trial_ends_at = trial_ends_at;
  if (subscription_status !== undefined) body.subscription_status = subscription_status;
  if (is_active !== undefined) body.is_active = is_active;

  const res = await apiFetch(`${API_ENDPOINTS.ADMIN_ORGANIZATIONS}/${orgId}/subscription`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to update organization subscription (${res.status})`);
  }
  return res.json();
}

/**
 * Generate a Coverage Report PDF for an organization.
 * Returns a Blob (PDF) for download.
 */
export async function generateCoverageReport(orgId, { start_date, end_date, mode }) {
  const res = await apiFetch(`${API_ENDPOINTS.ADMIN_REPORTS_COVERAGE}/${orgId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ start_date, end_date, mode }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to generate report (${res.status})`);
  }
  return res.blob();
}

/**
 * Resend credentials to a user — generates a new random password and emails it.
 * Previous password is invalidated.
 */
export async function resendCredentials(userId) {
  const res = await apiFetch(`${API_ENDPOINTS.ADMIN_RESEND_CREDENTIALS}/${userId}/resend-credentials`, {
    method: "POST",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to resend credentials (${res.status})`);
  }
  return res.json();
}

/**
 * Directly register a new organization and user without the access request flow.
 * Returns { user_id, email, org_id, org_name, market_id, market_city, market_state, message }.
 */
export async function directRegister({ email, full_name, city, state, org_name, admin_notes } = {}) {
  const body = { email, full_name, city, state };
  if (org_name) body.org_name = org_name;
  if (admin_notes) body.admin_notes = admin_notes;

  const res = await apiFetch(API_ENDPOINTS.ADMIN_DIRECT_REGISTER, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to register account (${res.status})`);
  }
  return res.json();
}

/**
 * Clear all clients and deals for a specific organization.
 * Returns { deleted_clients, deleted_deals }.
 */
export async function clearAllClientData(orgId) {
  const res = await apiFetch(`${API_ENDPOINTS.CLIENTS_CLEAR_ALL}?org_id=${orgId}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to clear client data (${res.status})`);
  }
  return res.json();
}

/**
 * Preview coverage metrics (JSON) without generating the PDF.
 */
export async function previewCoverageMetrics(orgId, { start_date, end_date, mode }) {
  const res = await apiFetch(`${API_ENDPOINTS.ADMIN_REPORTS_COVERAGE}/${orgId}/preview`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ start_date, end_date, mode }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to preview metrics (${res.status})`);
  }
  return res.json();
}
